import { GlobalStyles } from '@zeus/shared';
import { getResponsiveFontStyles } from '../sections/Commons/helpers';

interface FallbackStyles {
  fontSize?: number;
  lineHeight?: number;
  fontWeight?: number;
  fontStyle?: string;
  letterSpacing?: number;
  textStroke?: number | string;
  'media-s'?: {
    fontSize?: number;
    lineHeight?: number;
    fontWeight?: number;
    fontStyle?: string;
    letterSpacing?: number;
    textStroke?: number | string;
  };
}

/**
 * Normalizes a key by converting to lowercase and removing special characters
 * This helps match style keys like 'subHeading' with global style keys like 'Sub-Heading'
 */
export const normalizeKey = (key: string): string => {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Maps normalized style keys to their corresponding global style keys
 */
const STYLE_KEY_MAPPING: Record<string, keyof GlobalStyles> = {
  heading: 'heading',
  subheading: 'subHeading',
  bodytext: 'bodyText',
  overline: 'bodyText', // overline uses bodyText global styles
  navbarLinkStyles: 'bodyText',
  button: 'button',
  secondarybutton: 'button', // secondary button also uses button global styles
  // CTA section specific mappings
  buttonbackground: 'button',
  buttontext: 'button',
  secondarybuttonbackground: 'button',
  secondarybuttontext: 'button',
  description: 'bodyText', // CTA section uses 'description' key for bodyText
  cardheading: 'heading',
  cardsubheading: 'subHeading',
  cardbodytext: 'bodyText',
  cardoverline: 'bodyText',
  cardbutton: 'button',
  cardsecondarybutton: 'button',
  carddescription: 'bodyText',
};

/**
 * Gets the corresponding global style key for a given style key
 */
export const getGlobalStyleKey = (styleKey: string): keyof GlobalStyles | undefined => {
  const normalizedKey = normalizeKey(styleKey);
  return STYLE_KEY_MAPPING[normalizedKey];
};

export const getMediaKey = (screenSize: string) => {
  if (screenSize === 'MOBILE') {
    return 'media-s';
  }
  return undefined;
};

export const getResponsiveStyles = (componentProps: any, globalStyles: any, screenSize: string) => {
  const mediaKey = getMediaKey(screenSize);

  // Get component styles - don't fall back to global, just return what's configured
  const componentStyles = componentProps[mediaKey || 'media-s']?.styles || {};
  const componentConfig = componentProps[mediaKey || 'media-s']?.config || {};

  return {
    componentStyles,
    componentConfig,
    globalStyles,
  };
};

/**
 * Generates the effective inline styles by resolving values using the standard display value logic.
 * This ensures consistency between the config panel and the renderer.
 */
export const getEffectiveInlineStyles = (
  dataKey: string,
  componentStyles: any, // The full component styles object
  globalStyles: any,
  appliedStyles: any,
  globalStyleKeyMapping?: string
) => {
  const keyParts = dataKey.split('.');
  let key = keyParts[0];

  const itemsIndex = keyParts.indexOf('items') >= 0 ? keyParts.indexOf('items') : keyParts.indexOf('value'); // Get the index of 'items' for (Info Cards) or 'value' for (MainComponent/Hero Carousel)
  if (itemsIndex !== -1 && itemsIndex + 2 < keyParts.length) {
    key = keyParts[itemsIndex + 2];
  }

  // Determine global mapping
  const mapping = globalStyleKeyMapping || getGlobalStyleKey(key) || 'bodyText';
  const elementKey = key;

  let nestedElementStyles: any = {};

  // Check for nested structure and extract styles
  if (itemsIndex !== -1 && itemsIndex + 2 < keyParts.length) {
    // 2. Get Nested Element Styles (Specific to the item)
    let current: any = componentStyles;
    for (let i = 0; i < itemsIndex + 2; i++) {
      // Safe navigation
      if (current && current[keyParts[i]]) {
        current = current[keyParts[i]];
      } else {
        current = undefined;
        break;
      }
    }
    nestedElementStyles = current || {};
  } else {
    // Handle top-level elements: context is the root styles object
    nestedElementStyles = componentStyles || {};
  }

  // Merge applied styles into the specific element's style object
  // appliedStyles represents the style OF the element, so it must be merged into nestedElementStyles[elementKey]
  // Merge applied styles into the specific element's style object
  // appliedStyles represents the style OF the element, so it must be merged into nestedElementStyles[elementKey]
  // Fallback to nestedElementStyles root if specific key lookup fails (handles different drill-down depths)
  const baseElementStyles = nestedElementStyles[elementKey] || nestedElementStyles || {};

  const effectiveElementStyles = {
    ...baseElementStyles,
    ...appliedStyles,
    'media-s': {
      ...(baseElementStyles['media-s'] || {}),
      ...(appliedStyles['media-s'] || {}),
    },
  };

  const effectiveNestedStyles = {
    ...nestedElementStyles,
    [elementKey]: effectiveElementStyles,
  };

  const properties = [
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'lineHeight',
    'color',
    'effect',
    'textStroke',
    'effectColor',
    'effectIntensity',
  ];

  const resolveStyles = (mediaKey?: string) => {
    const resolved: any = {};
    properties.forEach((prop) => {
      resolved[prop] = getNestedElementDisplayValue(
        prop,
        mediaKey,
        effectiveNestedStyles,
        nestedElementStyles, // Use the correct parent scope for this element
        globalStyles,
        elementKey, // styleKey
        mapping
      );
      if (mediaKey === 'media-s') {
        resolved[prop] = effectiveNestedStyles[elementKey]?.[mediaKey]?.[prop] || resolved[prop];
      }
    });
    return resolved;
  };

  const mergedDesktop = resolveStyles(undefined);
  const mergedMobile = resolveStyles('media-s');

  return {
    mockStyles: {
      inlineText: {
        ...mergedDesktop,
        'media-s': mergedMobile,
      },
    },
    elementKey,
    globalMapping: mapping,
  };
};

// Function to get display value for customization UI
export const getDisplayValue = (
  componentProps: any,
  styleKey: string,
  property: string,
  globalStyles: any,
  mediaKey?: string,
  globalStyleKeyMapping?: string, // Optional custom mapping for global style key
  fallbackStyles?: FallbackStyles
) => {
  // Helper function to get value from global styles
  const getGlobalStyleValueLocal = (styleKeyLocal: string, propertyLocal: string) => {
    // Use custom mapping if provided, otherwise use the existing logic
    let globalStyleKey;
    if (globalStyleKeyMapping) {
      globalStyleKey = globalStyleKeyMapping;
    } else {
      globalStyleKey = getGlobalStyleKey(styleKeyLocal);
    }

    if (globalStyleKey && globalStyles?.[globalStyleKey] && typeof globalStyles[globalStyleKey] === 'object') {
      const styleObj = globalStyles[globalStyleKey] as Record<string, any>;
      return styleObj[propertyLocal];
    }
    return undefined;
  };

  if (mediaKey === 'media-s') {
    // First, try direct media-s value
    const mediaValue = componentProps?.[mediaKey]?.[styleKey]?.[property];
    if (mediaValue !== undefined) {
      return mediaValue;
    }

    // 1a) Try explicit mobile fallback if available
    // This handles the case where we have a specific mobile fallback (e.g. parent element's mobile style)
    // that should NOT be re-scaled.
    const mobileFallback = fallbackStyles?.['media-s']?.[property as keyof FallbackStyles['media-s']];
    if (mobileFallback !== undefined) {
      return mobileFallback;
    }

    // Centralize responsive handling for fontSize and lineHeight
    if (property === 'fontSize') {
      const desktopFontSize = componentProps?.[styleKey]?.fontSize;
      const globalFontSize = getGlobalStyleValueLocal(styleKey, 'fontSize');
      const fallbackFontSize = fallbackStyles?.fontSize ?? globalFontSize;
      const basisFont = desktopFontSize ?? fallbackFontSize;
      if (basisFont !== undefined) {
        const responsive = getResponsiveFontStyles(String(basisFont));
        return responsive?.mobile?.fontSize ?? basisFont;
      }
      return undefined;
    }

    if (property === 'lineHeight') {
      // Default mobile lineHeight to the resolved mobile fontSize when not explicitly set
      const mediaFontSize = componentProps?.[mediaKey]?.[styleKey]?.['fontSize'];
      if (mediaFontSize !== undefined) return mediaFontSize;

      // If mobile font size fallback exists, consider using it for line-height if appropriate?
      // But lineHeight has its own logic.

      const desktopFontSize = componentProps?.[styleKey]?.fontSize;
      const globalFontSize = getGlobalStyleValueLocal(styleKey, 'fontSize');
      const basisFont = desktopFontSize ?? globalFontSize;
      if (basisFont !== undefined) {
        const responsive = getResponsiveFontStyles(String(basisFont));
        return responsive?.mobile?.fontSize ?? basisFont;
      }

      // As a last resort, try desktop lineHeight/global lineHeight
      const desktopLine = componentProps?.[styleKey]?.lineHeight;
      if (desktopLine !== undefined) return desktopLine;
      const globalLine = getGlobalStyleValueLocal(styleKey, 'lineHeight');
      return globalLine;
    }

    // For other properties, keep previous behavior: media-s -> desktop -> global
    const desktopValue = componentProps?.[styleKey]?.[property];
    if (desktopValue !== undefined) return desktopValue;
    return getGlobalStyleValueLocal(styleKey, property);
  } else {
    // Desktop: desktop -> global
    const desktopValue = componentProps?.[styleKey]?.[property];
    if (desktopValue !== undefined) return desktopValue;
    if (fallbackStyles) {
      return fallbackStyles[property as keyof FallbackStyles];
    }
    return getGlobalStyleValueLocal(styleKey, property);
  }
};

// Helper function to get global style value for nested elements
export const getGlobalStyleValue = (
  styleKey: string,
  property: string,
  globalStyles: any,
  globalStyleKeyMapping?: string
) => {
  let globalStyleKey;
  if (globalStyleKeyMapping) {
    globalStyleKey = globalStyleKeyMapping;
  } else {
    globalStyleKey = getGlobalStyleKey(styleKey);
  }

  if (globalStyleKey && globalStyles?.[globalStyleKey] && typeof globalStyles[globalStyleKey] === 'object') {
    const styleObj = globalStyles[globalStyleKey] as Record<string, any>;
    return styleObj[property];
  }
  return undefined;
};

// Helper function for nested element styling hierarchy
export const getNestedElementDisplayValue = (
  property: string,
  mediaKey?: string,
  nestedElementStyles?: any,
  parentStyles?: any,
  globalStyles?: any,
  styleKey?: string,
  globalStyleKeyMapping?: string
) => {
  if (mediaKey === 'media-s') {
    // 1) Try media-s nested element styles
    if (nestedElementStyles?.[styleKey || '']?.[mediaKey]?.[property]) {
      return nestedElementStyles[styleKey || ''][mediaKey][property];
    }

    // 2) Try media-s parent component styles (parentStyles['media-s'][styleKey][property])
    if (parentStyles?.[mediaKey || 'media-s']?.[styleKey || '']?.[property]) {
      return parentStyles[mediaKey || 'media-s'][styleKey || ''][property];
    }

    // 2a) For lineHeight, if parent media-s lineHeight is not set, use parent media-s fontSize if present
    if (property === 'lineHeight') {
      const parentMobileFontSize = parentStyles?.[mediaKey || 'media-s']?.[styleKey || '']?.['fontSize'];
      if (parentMobileFontSize !== undefined) {
        return typeof parentMobileFontSize === 'string' ? parseInt(parentMobileFontSize) : parentMobileFontSize;
      }
    }

    // 3) Prefer auto-calc from parent desktop before global
    if (property === 'fontSize') {
      const parentDesktopFontSize = parentStyles?.[styleKey || '']?.fontSize;
      if (parentDesktopFontSize !== undefined) {
        const responsive = getResponsiveFontStyles(parentDesktopFontSize.toString());
        return responsive?.mobile?.fontSize ?? parentDesktopFontSize;
      }
    } else if (property === 'lineHeight') {
      const parentDesktopFontSize = parentStyles?.[styleKey || '']?.fontSize;
      const parentDesktopLineHeight = parentStyles?.[styleKey || '']?.lineHeight;
      if (parentDesktopFontSize !== undefined) {
        const responsive = getResponsiveFontStyles(parentDesktopFontSize.toString());
        const mobileLine = responsive?.mobile?.lineHeight ?? responsive?.mobile?.fontSize;
        if (mobileLine !== undefined) {
          return typeof mobileLine === 'string' ? parseInt(mobileLine) : mobileLine;
        }
      }
      if (parentDesktopLineHeight !== undefined) {
        return parentDesktopLineHeight;
      }
    } else {
      // Non-responsive properties: try nested or parent desktop before global
      if (nestedElementStyles?.[styleKey || '']?.[property]) {
        return nestedElementStyles[styleKey || ''][property];
      }
      if (parentStyles?.[styleKey || '']?.[property]) {
        return parentStyles[styleKey || ''][property];
      }
    }

    // 4) Auto-calc from global (fontSize/lineHeight only)
    const globalValue = getGlobalStyleValue(styleKey || '', property, globalStyles, globalStyleKeyMapping);
    if (globalValue && (property === 'fontSize' || property === 'lineHeight')) {
      if (property === 'fontSize') {
        const responsiveStyles = getResponsiveFontStyles(globalValue.toString());
        return responsiveStyles.mobile?.fontSize || globalValue;
      } else if (property === 'lineHeight') {
        const globalFontSize = getGlobalStyleValue(styleKey || '', 'fontSize', globalStyles, globalStyleKeyMapping);
        if (globalFontSize) {
          const responsiveStyles = getResponsiveFontStyles(globalFontSize.toString());
          const responsiveLineHeight = responsiveStyles.mobile?.lineHeight;
          if (responsiveLineHeight) {
            return parseInt(responsiveLineHeight);
          }
        }
        return globalValue;
      }
    }

    // 5) Final default for lineHeight: match resolved mobile fontSize if still unset
    if (property === 'lineHeight') {
      const nestedMobileFontSize = nestedElementStyles?.[styleKey || '']?.[mediaKey || 'media-s']?.['fontSize'];
      if (nestedMobileFontSize !== undefined) {
        return typeof nestedMobileFontSize === 'string' ? parseInt(nestedMobileFontSize) : nestedMobileFontSize;
      }
      const parentMobileFontSize = parentStyles?.[mediaKey || 'media-s']?.[styleKey || '']?.['fontSize'];
      if (parentMobileFontSize !== undefined) {
        return typeof parentMobileFontSize === 'string' ? parseInt(parentMobileFontSize) : parentMobileFontSize;
      }
    }

    // 6) Fall back to global styles
    return getGlobalStyleValue(styleKey || '', property, globalStyles, globalStyleKeyMapping);
  }

  // Desktop: nested -> parent -> global
  if (nestedElementStyles?.[styleKey || '']?.[property]) {
    return nestedElementStyles[styleKey || ''][property];
  }
  if (parentStyles?.[styleKey || '']?.[property]) {
    return parentStyles[styleKey || ''][property];
  }
  return getGlobalStyleValue(styleKey || '', property, globalStyles, globalStyleKeyMapping);
};

/**
 * Traverses the component properties to find the target style object based on the dataKey.
 * Handles nested structures like 'cards.items.0.heading'.
 */
export const getStylesFromPath = (componentProps: any, dataKey: string) => {
  const keyParts = dataKey.split('.');
  let styleKey = keyParts[0];
  let targetStyles = componentProps?.styles || {};

  if (keyParts.length > 1) {
    // If ends with 'text', take the one before it, otherwise assume the key itself is the style key
    const effectiveParts = keyParts[keyParts.length - 1] === 'text' ? keyParts.slice(0, -1) : keyParts;
    styleKey = effectiveParts[effectiveParts.length - 1];

    // Traverse to parent style object
    if (effectiveParts.length > 1) {
      const parentPath = effectiveParts.slice(0, -1);
      for (const p of parentPath) {
        if (targetStyles && targetStyles[p]) {
          targetStyles = targetStyles[p];
        } else {
          targetStyles[p] = {}; // Break chain if path not found
          targetStyles = targetStyles[p];
        }
      }
    }
  }
  return { targetStyles, styleKey };
};
